/**
 * @typedef {import('BMapsSrc/model').Compound} Compound
 */
import { structureKey } from 'BMapsModel';
import { ensureArray, conditionalObjectItem } from 'BMapsSrc/util/js_utils';
import * as targetActions from '.';
import store from '../store';
import {
    asIndividualTarget, getEntryId, getEntryMapCases, getTargetMapCases, isGroupEntry,
} from './entry';

// Re-export the pure, store-free entry helpers so callers can import either the
// helpers or the store-bound actions from one place (access).
export {
    asIndividualTarget, getEntryId, getEntryMapCases, getTargetMapCases, isGroupEntry,
};

export function addStructureToProjectTargets(mapCase, targetInfo={}) {
    store.dispatch(targetActions.addStructure(getTargetInfoForMapCase(mapCase, targetInfo)));
}

export function removeStructureByMapCase(mapCase) {
    if (mapCase) {
        store.dispatch(targetActions.removeStructure({ mapCase }));
        return;
    }
    console.log('Error: No mapCase provided to Redux');
}

/**
 * Add a named group of MapCases as a single target-list entry (e.g. a curated
 * kinase conformation panel). A group has no target/off-target flag — its members are
 * scored only when pulled out as individuals.
 * @param {string} groupId stable key, e.g. a curated panel id or mapCase.group_name
 * @param {string} displayName human-readable group name shown in the UI
 * @param {import('BMapsModel').MapCase[]} mapCases the group members
 * @param {{ source?: string }} [targetInfo]
 */
export function addGroupToProjectTargets(groupId, displayName, mapCases, targetInfo={}) {
    store.dispatch(targetActions.addGroup({
        groupId,
        displayName,
        mapCases,
        ...conditionalObjectItem(targetInfo.source != null, 'source', targetInfo.source),
    }));
}

/** @param {string} groupId */
export function removeGroupById(groupId) {
    store.dispatch(targetActions.removeGroup({ groupId }));
}

export function removeAllStructures() {
    store.dispatch(targetActions.removeAllStructures());
}

// use setProteinTarget in connectedDataCmds to also rebuild the protein tree
/**
 * @param {import('BMapsSrc/model').MapCase} mapCase
 * @param {{isTarget?: boolean}} targetInfo
 */
export function setProteinTarget(mapCase, targetInfo) {
    store.dispatch(targetActions.setProteinTarget(getTargetInfoForMapCase(mapCase, targetInfo)));
}

/**
 * @typedef {{ isTarget: boolean }} targetInfo
 * @typedef {{ mapCase: MapCase, targetInfo: { isTarget: boolean } }} mapCaseInfo
 * @param {mapCaseInfo[] | mapCaseInfo} mapCaseInfoList
 */
export function setAllTargetInfo(mapCaseInfoList) {
    const newTargetInfo = ensureArray(mapCaseInfoList);
    store.dispatch(targetActions.setAllTargetInfo(newTargetInfo));
}

/**
 * Update the stored alignment (raw USAlign transform + reference structureKey) on an existing
 * unloaded structure-of-interest target entry. Used by the recompute-on-stale pre-pass.
 * @param {import('BMapsSrc/model').MapCase} mapCase
 * @param {{ alignTransform: object, alignReferenceFrame: string }} alignment
 */
export function updateStructureAlignment(mapCase, { alignTransform, alignReferenceFrame }) {
    store.dispatch(
        targetActions.updateStructureAlignment({ mapCase, alignTransform, alignReferenceFrame }),
    );
}

export function getTargetInfoMap() {
    const map = new Map();
    const list = store.getState().projectState.targetList;
    // An individual entry wins over group membership for the same structure: a structure
    // pulled out of a curated (reference) panel as a target/off-target overrides the panel.
    // The map is still keyed by the MapCase OBJECT (consumers such as
    // ConnectedDataCmds.ensureUnloadedAlignments look entries up by object identity).
    const individualKeys = new Set(
        list.filter((entry) => !entry.groupId).map((entry) => structureKey(entry.mapCase))
    );
    list.forEach((entry) => {
        if (entry.groupId) return; // individuals first
        map.set(entry.mapCase, entry);
    });
    list.forEach((entry) => {
        if (!entry.groupId) return; // then group members, unless an individual covers them
        entry.mapCases.forEach((mc) => {
            if (individualKeys.has(structureKey(mc))) return;
            map.set(mc, entry);
        });
    });
    return map;
}

/**
 * The group (collection) entries in the target list — e.g. curated kinase panels.
 * A generic named group of structures, not kinase-specific.
 * @returns {import('./slice').GroupTarget[]}
 */
export function getTargetGroups() {
    return store.getState().projectState.targetList.filter(isGroupEntry);
}

/**
 * The individual (non-group) target entries — structures of interest added in their own right
 * (target / off-target), as opposed to curated panel groups. Store-backed mirror of
 * getTargetGroups(). For a target list already in hand, use targetList.filter(asIndividualTarget).
 * @returns {import('./slice').IndividualTarget[]}
 */
export function getIndividualTargets() {
    return store.getState().projectState.targetList.filter(asIndividualTarget);
}

function getTargetInfoForMapCase(mapCase, targetInfo={}) {
    const targetInfoToAdd = {
        isTarget: targetInfo.isTarget ?? true,
        // source: how the entry was added (manual / query / curated). Optional.
        ...conditionalObjectItem(targetInfo.source != null, 'source', targetInfo.source),
        // Alignment for an unloaded structure of interest: the raw USAlign
        // transform ({ rotation, translation }) to a reference frame, plus the
        // structureKey (mount-free id) of the reference it targets. oneCaseExtras
        // applies it for an unloaded target ONLY while that reference is still the
        // active query reference (a stale-frame transform would mis-place fragments).
        // Absent (older sessions / no loaded reference at add time) = unknown frame.
        ...conditionalObjectItem(
            targetInfo.alignTransform != null, 'alignTransform', targetInfo.alignTransform
        ),
        ...conditionalObjectItem(
            targetInfo.alignReferenceFrame != null, 'alignReferenceFrame', targetInfo.alignReferenceFrame
        ),
    };
    return {
        mapCase,
        displayName: mapCase.displayName,
        ...targetInfoToAdd,
    };
}

export function setCompoundTableState(compoundTableState) {
    store.dispatch(targetActions.setCompoundTableState(compoundTableState));
}

export function setScoringTableState(scoringTableState) {
    store.dispatch(targetActions.setScoringTableState(scoringTableState));
}

export function setJobsCompoundsTableState(jobsCompoundsTableState) {
    store.dispatch(targetActions.setJobsCompoundsTableState(jobsCompoundsTableState));
}

export function setJobsGridTableState(jobsGridTableState) {
    store.dispatch(targetActions.setJobsGridTableState(jobsGridTableState));
}

export function setScreeningTableState(screeningTableState) {
    store.dispatch(targetActions.setScreeningTableState(screeningTableState));
}
export function setSidePanelWidth(sidePanelWidth, table) {
    store.dispatch(targetActions.setSidePanelWidth({ table, sidePanelWidth }));
}

export function setSidePanelMinimized(minimized, table) {
    store.dispatch(targetActions.setSidePanelMinimized({ table, minimized }));
}

// ─── Per-pane setters + selector (D2, D3) ─────────────────────────────
// Introduced by `bmaps-sidepanel-independent-columns` (2026-05-28).
// The legacy single-key setters above remain unchanged for backward
// compatibility with non-stackable side panels.

/**
 * Persist a per-pane width under `tableState.<table>.panes.<paneId>.width`.
 * Used by `<PaneColumn>` on drag-end for stackable panes that maintain
 * independent widths.
 *
 * @param {string} paneId - The stackable pane's id (e.g., 'help', 'workflow')
 * @param {number} width - Width in CSS pixels
 * @param {string} table - Table key (e.g., 'rightSidePanel')
 */
export function setSidePanelPaneWidth(paneId, width, table) {
    store.dispatch(targetActions.setSidePanelPaneWidth({ table, paneId, width }));
}

/**
 * Persist a per-pane minimize flag under `tableState.<table>.panes.<paneId>.minimized`.
 * Note: rail mode itself remains column-level (per D7 of approach.md) —
 * this setter exists for future per-pane minimize UX, currently unused
 * but registered so the slice shape stays symmetric.
 *
 * @param {string} paneId
 * @param {boolean} minimized
 * @param {string} table
 */
export function setSidePanelPaneMinimized(paneId, minimized, table) {
    store.dispatch(targetActions.setSidePanelPaneMinimized({ table, paneId, minimized }));
}

/**
 * Read the per-pane map for a given table, applying migration from
 * legacy single-width persisted state. Always returns a plain object;
 * empty for first-time / unrecognized state.
 *
 * Imports the helper lazily to avoid a circular dep between the Redux
 * access layer and the UI helpers module.
 *
 * @param {string} table - Table key (e.g., 'rightSidePanel')
 * @param {string[]} [knownPaneIds] - Stackable pane ids to seed from legacy width
 * @returns {Object<string, {width: number, minimized: boolean}>}
 */
export function selectSidePanelPanes(table, knownPaneIds) {
    // eslint-disable-next-line global-require
    const { migratePersistedShape } = require('BMapsUI/info_display/sidePanelHelpers');
    const tableState = store.getState().projectState.tableState[table] || {};
    return migratePersistedShape(tableState, knownPaneIds);
}

export function getTableState() {
    return { ...store.getState().projectState.tableState };
}

export function setTableState(tableState) {
    store.dispatch(targetActions.setTableState(tableState));
}

export function setCoralMapState(coralMap, table) {
    store.dispatch(targetActions.setCoralMapState({ coralMap, table }));
}

export function updateScreeningPropertySettings(settings) {
    store.dispatch(targetActions.updateScreeningPropertySettings(settings));
}

export function setActiveChart(chart, table) {
    store.dispatch(targetActions.setActiveChart({ chart, table }));
}

export function setTargetsTableState(targetsTableState) {
    store.dispatch(targetActions.setTargetsTableState(targetsTableState));
}
